import { useEffect, useState } from 'react';
import type {
  FinanceRate,
  ModalidadTarifaTrabajo,
  PropiedadResumen,
  TipoHora,
  UsuarioSesion,
} from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onRegistrado: () => void;
}

interface UsuarioInterno {
  id: string;
  nombre: string;
  rol: string;
}

const hoy = () => new Date().toISOString().slice(0, 10);

/**
 * Registro de horas y gastos. Alimenta el resumen mensual: mientras no se
 * registra nada, el resumen está vacío. La Dirección puede imputar horas a otra
 * persona; un manager registra las suyas.
 */
export function FinanzasRegistro({ usuario, idioma, onRegistrado }: Props) {
  const esDireccion = usuario.rol === 'DIRECTOR';
  const [propiedades, setPropiedades] = useState<PropiedadResumen[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioInterno[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  // --- Tarifas (sólo Dirección) ---
  const [tarifas, setTarifas] = useState<FinanceRate[]>([]);
  const [tUser, setTUser] = useState(usuario.id);
  const [tProp, setTProp] = useState('');
  const [tModalidad, setTModalidad] = useState<ModalidadTarifaTrabajo>('POR_HORA');
  const [tImporte, setTImporte] = useState('');

  function cargarTarifas() {
    if (!esDireccion) return;
    api
      .finTarifas()
      .then(({ tarifas: lista }) => setTarifas(lista))
      .catch(() => {});
  }

  // --- Formulario de horas ---
  const [hProp, setHProp] = useState('');
  const [hUser, setHUser] = useState(usuario.id);
  const [hFecha, setHFecha] = useState(hoy);
  const [hHoras, setHHoras] = useState('');
  const [hTipo, setHTipo] = useState<TipoHora>('GESTION');
  const [hExtra, setHExtra] = useState(false);

  // --- Formulario de gastos ---
  const [gProp, setGProp] = useState('');
  const [gFecha, setGFecha] = useState(hoy);
  const [gConcepto, setGConcepto] = useState('');
  const [gCategoria, setGCategoria] = useState('OTRO');
  const [gCoste, setGCoste] = useState('');
  const [gMargen, setGMargen] = useState('');
  /*
    LA FACTURA VIENE CON EL GASTO.
    Un gasto sin comprobante es un numero que nadie puede comprobar despues. La
    carga rapida ya lo pedia; este formulario, que es por donde entra Direccion,
    no lo pedia, y por eso habia gastos huerfanos de factura en la lista.
  */
  const [gFactura, setGFactura] = useState<File | null>(null);

  useEffect(() => {
    api
      .propiedades()
      .then(({ propiedades: p }) => setPropiedades(p))
      .catch(() => {});
    if (esDireccion) {
      api
        .usuarios()
        .then(({ usuarios: u }) => setUsuarios(u))
        .catch(() => {});
      cargarTarifas();
    }
  }, [esDireccion]);

  async function fijarTarifa() {
    const importe = Number(tImporte.replace(',', '.'));
    if (!Number.isFinite(importe) || importe < 0) {
      setError(t(idioma, 'finreg.indicaImporte'));
      return;
    }
    setError(null);
    setOk(null);
    try {
      await api.finFijarTarifa({
        userId: tUser,
        modalidad: tModalidad,
        importeCents: Math.round(importe * 100),
        ...(tProp ? { propertyId: tProp } : { propertyId: null }),
      });
      setTImporte('');
      setOk(t(idioma, 'finreg.tarifaFijada'));
      cargarTarifas();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'finreg.noSeFijo'));
    }
  }

  async function registrarHoras() {
    setError(null);
    setOk(null);
    const horas = Number(hHoras.replace(',', '.'));
    if (!Number.isFinite(horas) || horas <= 0) {
      setError(t(idioma, 'finreg.indicaHoras'));
      return;
    }
    try {
      await api.finRegistrarHoras({
        fecha: hFecha,
        minutos: Math.round(horas * 60),
        tipo: hTipo,
        ...(hProp ? { propertyId: hProp, facturableExtra: hExtra } : {}),
        ...(esDireccion && hUser !== usuario.id ? { userId: hUser } : {}),
      });
      setHHoras('');
      setOk(t(idioma, 'finreg.horasRegistradas'));
      onRegistrado();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'finreg.noSeRegistraronHoras'));
    }
  }

  async function registrarGasto() {
    setError(null);
    setOk(null);
    const coste = Number(gCoste.replace(',', '.'));
    if (!Number.isFinite(coste) || coste < 0 || gConcepto.trim().length < 2) {
      setError(t(idioma, 'finreg.indicaGasto'));
      return;
    }
    const margen = Number(gMargen.replace(',', '.'));
    try {
      const creado = await api.finRegistrarGasto({
        fecha: gFecha,
        concepto: gConcepto.trim(),
        categoria: gCategoria as 'LOGISTICA' | 'COMPRA' | 'OTRO',
        costeCents: Math.round(coste * 100),
        ...(gProp ? { propertyId: gProp } : {}),
        ...(Number.isFinite(margen) && margen > 0 ? { margenPb: Math.round(margen * 100) } : {}),
      });
      if (gFactura) {
        // Si la factura no sube, el gasto YA esta registrado: se avisa, pero no
        // se finge que ha fallado todo.
        try {
          await api.subirAdjunto('GASTO', creado.gasto.id, gFactura);
        } catch {
          setError(t(idioma, 'finreg.facturaNoSubio'));
        }
      }
      setGConcepto('');
      setGCoste('');
      setGMargen('');
      setGFactura(null);
      setOk(t(idioma, 'finreg.gastoRegistrado'));
      onRegistrado();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'finreg.noSeRegistroGasto'));
    }
  }

  return (
    <section className="tarjeta fin-registro">
      <h2>{t(idioma, 'finreg.titulo')}</h2>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {ok && <p className="ok-mensaje">{ok}</p>}

      <div className="fin-registro-cols">
        {/* Horas */}
        <div className="fin-form">
          <h3>{t(idioma, 'finreg.horas')}</h3>
          {esDireccion && (
            <div className="campo">
              <label htmlFor="h-user">{t(idioma, 'finreg.persona')}</label>
              <select id="h-user" value={hUser} onChange={(e) => setHUser(e.target.value)}>
                <option value={usuario.id}>
                  {t(idioma, 'finreg.yo', { nombre: usuario.nombre })}
                </option>
                {usuarios
                  .filter((u) => u.id !== usuario.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre}
                    </option>
                  ))}
              </select>
            </div>
          )}
          <div className="campo">
            <label htmlFor="h-prop">{t(idioma, 'finreg.propiedad')}</label>
            <select id="h-prop" value={hProp} onChange={(e) => setHProp(e.target.value)}>
              <option value="">{t(idioma, 'finreg.general')}</option>
              {propiedades.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo} · {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="h-fecha">{t(idioma, 'finreg.fecha')}</label>
            <input id="h-fecha" type="date" value={hFecha} onChange={(e) => setHFecha(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="h-horas">{t(idioma, 'finreg.horas')}</label>
            <input
              id="h-horas"
              inputMode="decimal"
              value={hHoras}
              placeholder="1,5"
              onChange={(e) => setHHoras(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="h-tipo">{t(idioma, 'finreg.tipo')}</label>
            <select id="h-tipo" value={hTipo} onChange={(e) => setHTipo(e.target.value as TipoHora)}>
              <option value="GESTION">{t(idioma, 'finreg.tipoGESTION')}</option>
              <option value="OTRO">{t(idioma, 'finreg.tipoOTRO')}</option>
            </select>
          </div>
          {hProp && (
            <label className="marcable-linea">
              <input type="checkbox" checked={hExtra} onChange={(e) => setHExtra(e.target.checked)} />
              <span>
                <b>{t(idioma, 'rapido.facturableExtra')}</b>{' '}
                <span className="suave">{t(idioma, hExtra ? 'rapido.extraSi' : 'rapido.extraNo')}</span>
              </span>
            </label>
          )}
          <button type="button" onClick={() => void registrarHoras()}>
            {t(idioma, 'finreg.registrarHoras')}
          </button>
        </div>

        {/* Gastos */}
        <div className="fin-form">
          <h3>{t(idioma, 'finreg.gasto')}</h3>
          <div className="campo">
            <label htmlFor="g-prop">{t(idioma, 'finreg.propiedad')}</label>
            <select id="g-prop" value={gProp} onChange={(e) => setGProp(e.target.value)}>
              <option value="">{t(idioma, 'finreg.general')}</option>
              {propiedades.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.codigo} · {p.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="g-fecha">{t(idioma, 'finreg.fecha')}</label>
            <input id="g-fecha" type="date" value={gFecha} onChange={(e) => setGFecha(e.target.value)} />
          </div>
          <div className="campo">
            <label htmlFor="g-concepto">{t(idioma, 'finreg.concepto')}</label>
            <input
              id="g-concepto"
              value={gConcepto}
              placeholder={t(idioma, 'finreg.conceptoPlaceholder')}
              onChange={(e) => setGConcepto(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="g-cat">{t(idioma, 'finreg.categoria')}</label>
            <select id="g-cat" value={gCategoria} onChange={(e) => setGCategoria(e.target.value)}>
              <option value="LOGISTICA">{t(idioma, 'finreg.catLOGISTICA')}</option>
              <option value="COMPRA">{t(idioma, 'finreg.catCOMPRA')}</option>
              <option value="OTRO">{t(idioma, 'finreg.catOTRO')}</option>
            </select>
          </div>
          <div className="fin-form-fila">
            <div className="campo">
              <label htmlFor="g-coste">{t(idioma, 'finreg.coste')}</label>
              <input
                id="g-coste"
                inputMode="decimal"
                value={gCoste}
                placeholder="0"
                onChange={(e) => setGCoste(e.target.value)}
              />
            </div>
            <div className="campo">
              <label htmlFor="g-margen">{t(idioma, 'finreg.margen')}</label>
              <input
                id="g-margen"
                inputMode="decimal"
                value={gMargen}
                placeholder="0"
                onChange={(e) => setGMargen(e.target.value)}
              />
            </div>
          </div>
          <div className="campo">
            <label htmlFor="g-factura">{t(idioma, 'gasto.adjuntarFactura')}</label>
            <input
              id="g-factura"
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setGFactura(e.target.files?.[0] ?? null)}
            />
          </div>
          <button type="button" onClick={() => void registrarGasto()}>
            {t(idioma, 'finreg.registrarGasto')}
          </button>
        </div>
      </div>

      {esDireccion && (
        <div className="fin-tarifas">
          <h3>{t(idioma, 'finreg.tarifas')}</h3>
          <p className="int-desc">{t(idioma, 'finreg.tarifasPie')}</p>
          {/*
            «Sin propiedad» hacía dos trabajos y no lo decía ninguno: es la
            tarifa de las HORAS INTERNAS —las que no cuelgan de ninguna casa— y,
            a la vez, la que se usa cuando una casa no tiene tarifa propia.
          */}
          <p className="int-desc">{t(idioma, 'finreg.tarifasSinPropiedad')}</p>
          {tarifas.length > 0 && (
            <ul className="fin-tarifas-lista">
              {tarifas.map((tarifa) => (
                <li key={tarifa.id}>
                  <span>{tarifa.userNombre ?? '—'}</span>
                  <span className="int-desc">
                    {tarifa.propertyNombre ?? t(idioma, 'finreg.horasInternas')}
                  </span>
                  <span>
                    {t(
                      idioma,
                      tarifa.modalidad === 'FIJA_MENSUAL'
                        ? 'finreg.fijaMesCorto'
                        : tarifa.modalidad === 'PRECIO_CLIENTE'
                          ? 'finreg.precioCliente'
                          : 'finreg.porHora',
                    )}
                  </span>
                  <span className="fin-num">
                    {formatearImporteDesdeCents(tarifa.importeCents, idioma, {
                      moneda: tarifa.moneda,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="fin-tarifa-form">
            <div className="campo">
              <label htmlFor="t-user">{t(idioma, 'finreg.persona')}</label>
              <select id="t-user" value={tUser} onChange={(e) => setTUser(e.target.value)}>
                <option value={usuario.id}>{usuario.nombre}</option>
                {usuarios
                  .filter((u) => u.id !== usuario.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nombre}
                    </option>
                  ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="t-prop">{t(idioma, 'finreg.propiedad')}</label>
              <select id="t-prop" value={tProp} onChange={(e) => setTProp(e.target.value)}>
                <option value="">{t(idioma, 'finreg.horasInternasOpcion')}</option>
                {propiedades.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.codigo} · {p.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div className="campo">
              <label htmlFor="t-mod">{t(idioma, 'finreg.modalidad')}</label>
              <select
                id="t-mod"
                value={tModalidad}
                onChange={(e) => setTModalidad(e.target.value as ModalidadTarifaTrabajo)}
              >
                <option value="POR_HORA">{t(idioma, 'finreg.porHora')}</option>
                <option value="PRECIO_CLIENTE">{t(idioma, 'finreg.precioCliente')}</option>
                <option value="FIJA_MENSUAL">{t(idioma, 'finreg.fijaMes')}</option>
              </select>
            </div>
            <div className="campo">
              <label htmlFor="t-imp">{t(idioma, 'finreg.importe')}</label>
              <input
                id="t-imp"
                inputMode="decimal"
                value={tImporte}
                placeholder="0"
                onChange={(e) => setTImporte(e.target.value)}
              />
            </div>
            <button type="button" onClick={() => void fijarTarifa()}>
              {t(idioma, 'finreg.fijarTarifa')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
